import type { SilobagListFilter } from '@siloreal:shared:api/service/silobag/silobag-list';
import type { DropdownOptionBadgePreset } from '@siloreal:shared:ui/basic/dropdown';
import type { SilobagTab, SilosProTab } from '../../../hook/use-silobag-list-logic-state';
import { useLocale } from '@siloreal:shared:hooks';

export type ExportableSilobagList =
  | { view: 'silospro'; tab: SilosProTab }
  | { view: 'silobag'; tab: Exclude<SilobagTab, 'archived'> };

export type ExportMode = 'list' | 'selection' | 'filtered';
export type ExportKind = 'quality-samples' | 'report' | 'satellite-certificate';

export type ExportMenuItem = {
  id: string;
  kind: ExportKind;
  mode: ExportMode;
  label: string;
  format: DropdownOptionBadgePreset;
};

export type ExportMenuGroup = {
  key: string;
  title: string;
  items: readonly ExportMenuItem[];
};

export type ExportListConfig = {
  filters: SilobagListFilter;
  fileLabel: string;
  groups: readonly ExportMenuGroup[];
};

type ExportListKey =
  | 'silospro:vrr'
  | 'silospro:no-vrr'
  | 'silospro:closed'
  | 'silobag:active'
  | 'silobag:closed';

type BuildExportGroupsOptions = {
  qualitySamples: string;
  report: string;
  satelliteCertificate?: string;
};

function buildModeItems(
  kind: ExportKind,
  idPrefix: string,
  label: string,
  format: DropdownOptionBadgePreset
): ExportMenuItem[] {
  const i18n = useLocale.getState();

  return [
    {
      id: `${idPrefix}-list`,
      kind,
      mode: 'list',
      label,
      format,
    },
    {
      id: `${idPrefix}-selection`,
      kind,
      mode: 'selection',
      label: `${label} ${i18n.t('temp.private.list.common.export.in_selection')}`,
      format,
    },
    {
      id: `${idPrefix}-filtered`,
      kind,
      mode: 'filtered',
      label: `${label} ${i18n.t('temp.private.list.common.export.with_active_filter')}`,
      format,
    },
  ];
}

function buildExportGroups(options: BuildExportGroupsOptions): ExportMenuGroup[] {
  const i18n = useLocale.getState();

  const exportItems = [
    ...buildModeItems('report', 'report', options.report, 'excel'),
    ...(options.satelliteCertificate
      ? buildModeItems('satellite-certificate', 'satellite', options.satelliteCertificate, 'pdf')
      : []),
  ];

  return [
    {
      key: 'quality-samples',
      title: i18n.t('temp.common.quality_sample'),
      items: buildModeItems('quality-samples', 'quality', options.qualitySamples, 'excel'),
    },
    {
      key: 'exports',
      title: i18n.t('temp.common.exports'),
      items: exportItems,
    },
  ];
}

export function getExportConfig(list: ExportableSilobagList): ExportListConfig {
  const i18n = useLocale.getState();
  const EXPORT_CONFIG = {
    'silospro:vrr': {
      filters: { active: true, vrr: true },
      fileLabel: 'SilosPro con VRR',
      groups: buildExportGroups({
        qualitySamples: i18n.t('temp.private.list.silospro.export.with_vrr.spreadsheet'),
        report: i18n.t('temp.private.list.silospro.export.with_vrr.report'),
        satelliteCertificate: i18n.t('temp.private.list.silospro.export.with_vrr.satellite_certificate'),
      }),
    },
    'silospro:no-vrr': {
      filters: { active: true, silobag_data_status_id: 2, vrr: false },
      fileLabel: 'SilosPro sin VRR',
      groups: buildExportGroups({
        qualitySamples: i18n.t('temp.private.list.silospro.export.without_vrr.spreadsheet'),
        report: i18n.t('temp.private.list.silospro.export.without_vrr.report'),
        satelliteCertificate: i18n.t('temp.private.list.silospro.export.without_vrr.satellite_certificate'),
      }),
    },
    'silospro:closed': {
      filters: { active: false, silobag_data_status_id: 2 },
      fileLabel: 'SilosPro cerrados',
      groups: buildExportGroups({
        qualitySamples: i18n.t('temp.private.list.silospro.export.closed.spreadsheet'),
        report: i18n.t('temp.private.list.silospro.export.closed.report'),
      }),
    },
    'silobag:active': {
      filters: { active: true, silobag_data_status_id: 1 },
      fileLabel: 'Silobolsas activos',
      groups: buildExportGroups({
        qualitySamples: i18n.t('temp.private.list.silobag.export.active.spreadsheet'),
        report: i18n.t('temp.private.list.silobag.export.active.report'),
      }),
    },
    'silobag:closed': {
      filters: { active: false, silobag_data_status_id: 1 },
      fileLabel: 'Silobolsas cerrados',
      groups: buildExportGroups({
        qualitySamples: i18n.t('temp.private.list.silobag.export.closed.spreadsheet'),
        report: i18n.t('temp.private.list.silobag.export.closed.report'),
      }),
    },
  } as const satisfies Record<ExportListKey, ExportListConfig>;

  return EXPORT_CONFIG[`${list.view}:${list.tab}` as ExportListKey];
}

export function getExportItems(config: ExportListConfig): readonly ExportMenuItem[] {
  return config.groups.flatMap((group) => group.items);
}
